import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import '../../styles/tutorial.css';
import {
  IcoZap, IcoUsers, IcoBuilding, IcoDollar, IcoUmbrella, IcoCalendar,
  IcoSettings, IcoClock, IcoTrash, IcoSave, IcoFolder, IcoRefreshCw,
  IcoGrid, IcoClipboard, IcoBarChart, IcoCheck, IcoFileText,
} from '../dashboard/DashIcons';

// ── Inline SVGs used only in the Schedule section ────────────────────────────

const SvgArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const SvgArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const SvgToggleOff = () => (
  <svg width="28" height="16" viewBox="0 0 48 28" fill="none">
    <rect x="0" y="0" width="48" height="28" rx="14" fill="#d1d5db"/>
    <circle cx="14" cy="14" r="10" fill="white"/>
  </svg>
);
const SvgToggleOn = () => (
  <svg width="28" height="16" viewBox="0 0 48 28" fill="none">
    <rect x="0" y="0" width="48" height="28" rx="14" fill="#22c55e"/>
    <circle cx="34" cy="14" r="10" fill="white"/>
  </svg>
);
const SvgGridCell = () => (
  <svg width="80" height="36" viewBox="0 0 120 48" fill="none">
    <rect x="0" y="0" width="120" height="48" rx="6" fill="#f0f4ff" stroke="#c7d2fe" strokeWidth="1.5"/>
    <text x="10" y="22" fontSize="10" fill="#6b7280" fontFamily="system-ui">Start</text>
    <rect x="8" y="28" width="46" height="12" rx="3" fill="white" stroke="#a5b4fc" strokeWidth="1"/>
    <text x="14" y="37" fontSize="8" fill="#4f46e5" fontFamily="system-ui">09:00</text>
    <text x="64" y="22" fontSize="10" fill="#6b7280" fontFamily="system-ui">End</text>
    <rect x="62" y="28" width="46" height="12" rx="3" fill="white" stroke="#a5b4fc" strokeWidth="1"/>
    <text x="68" y="37" fontSize="8" fill="#4f46e5" fontFamily="system-ui">05:00</text>
  </svg>
);
const SvgScheduleGrid = () => (
  <svg width="100%" viewBox="0 0 500 130" fill="none" style={{ maxWidth: 520 }}>
    {/* Header row */}
    <rect x="0" y="0" width="500" height="28" rx="4" fill="#1e2a4a"/>
    <text x="75" y="18" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Mon</text>
    <text x="145" y="18" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Tue</text>
    <text x="215" y="18" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Wed</text>
    <text x="285" y="18" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Thu</text>
    <text x="355" y="18" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Fri</text>
    <text x="425" y="18" fontSize="10" fill="white" fontFamily="system-ui" fontWeight="600">Sat</text>
    {/* Employee rows */}
    {[
      { name: 'Alex R.', y: 36, shifts: ['9–5','9–5','','9–5','9–5',''] },
      { name: 'Jamie L.', y: 72, shifts: ['','10–6','10–6','10–6','','10–6'] },
      { name: 'Morgan S.', y: 108, shifts: ['8–4','8–4','8–4','','8–4',''] },
    ].map(({ name, y, shifts }) => (
      <g key={name}>
        <rect x="0" y={y} width="60" height="28" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="0.5"/>
        <text x="4" y={y + 17} fontSize="8" fill="#374151" fontFamily="system-ui">{name}</text>
        {shifts.map((s, i) => (
          <g key={i}>
            <rect x={64 + i * 70} y={y} width="62" height="28"
              fill={s ? '#eff6ff' : '#f9fafb'} stroke="#e5e7eb" strokeWidth="0.5"/>
            {s && <text x={64 + i * 70 + 8} y={y + 17} fontSize="8" fill="#1d4ed8" fontFamily="system-ui">{s}</text>}
          </g>
        ))}
      </g>
    ))}
  </svg>
);

// ── Button chip for the reference list ───────────────────────────────────────
function BtnChip({ children, bg = '#1e2a4a', color = 'white', border }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem',
      fontWeight: '700', background: bg, color,
      border: border ? `1.5px solid ${border}` : 'none',
      whiteSpace: 'nowrap', verticalAlign: 'middle',
    }}>
      {children}
    </span>
  );
}

// ── Ref item row ──────────────────────────────────────────────────────────────
function RefItem({ chip, desc }) {
  return (
    <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start', padding: '0.6rem 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ flexShrink: 0, paddingTop: '0.1rem' }}>{chip}</div>
      <p style={{ margin: 0, fontSize: '0.93rem', color: '#374151', lineHeight: 1.55 }}>{desc}</p>
    </div>
  );
}

// ── Data ─────────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'getting-started',
    icon: <IcoZap size={28} color="#22c55e"/>,
    title: 'Getting Started',
    roles: 'all',
    blocks: [
      {
        heading: 'Welcome to ClockEarnings',
        steps: [
          'After logging in your Dashboard shows an overview of your organization — active employees, pending approvals, and recent activity.',
          'Use the top navigation bar to access all features: Dashboard, My Team, Schedule, Time Entry, Payroll, and PTO.',
          'On mobile, tap the menu icon in the header to open the navigation menu.',
          'Your account settings are accessible by clicking your name or avatar in the top-right corner.',
        ],
      },
      {
        heading: 'Your Role & Permissions',
        steps: [
          'Super Admin — full access to all features, settings, billing, and employee management.',
          'Admin Assistant — similar to Super Admin but cannot change billing or subscription settings.',
          'Manager — can manage their assigned team, approve time entries and PTO, and run payroll for their team.',
          'Employee — can view their own time cards, request PTO, and view the schedule.',
        ],
      },
    ],
  },
  {
    id: 'team',
    icon: <IcoUsers size={28} color="#22c55e"/>,
    title: 'Managing Your Team',
    roles: ['superadmin', 'adminassistant', 'manager'],
    blocks: [
      {
        heading: 'Adding a New Employee',
        steps: [
          'Go to My Team in the navigation, then click Add Employee.',
          'Enter the employee\'s first name, last name, and work email address.',
          'Select their Role — Employee, Team Lead, Manager, or Admin Assistant.',
          'Set their Employment Type (Full-time, Part-time, or Salaried) — this affects PTO accrual.',
          'Enter their hourly rate and start date.',
          'Optionally assign them to a manager and one or more departments.',
          'Click Save. Then open their profile and click Send Invitation to email them a link to set their password.',
        ],
      },
      {
        heading: 'Editing Employee Details',
        steps: [
          'Go to My Team and click on any employee\'s name to open their profile.',
          'Click Edit Employee (visible to admins and admin assistants).',
          'Update any fields — name, role, employment type, hourly rate, departments, or manager.',
          'Click Save Changes. Updates take effect immediately.',
          'Employment type changes update the employee\'s PTO policy allocation going forward.',
        ],
      },
      {
        heading: 'Deactivating an Employee',
        steps: [
          'Open the employee\'s profile page.',
          'Click the Deactivate button (visible to admins and admin assistants).',
          'Deactivated employees can no longer log in but their records are preserved.',
          'You can reactivate an employee at any time from their profile.',
        ],
      },
    ],
  },
  {
    id: 'departments',
    icon: <IcoBuilding size={28} color="#22c55e"/>,
    title: 'Departments & Managers',
    roles: ['superadmin', 'adminassistant', 'manager'],
    blocks: [
      {
        heading: 'Creating Departments',
        steps: [
          'Go to Departments in the navigation.',
          'Click Add Department and enter a name (e.g., Operations, Sales, HR, Warehouse).',
          'From any employee\'s profile page, toggle their department assignments.',
          'Employees can belong to multiple departments.',
          'Departments help organize your team directory and filter reports.',
        ],
      },
      {
        heading: 'Setting Up Managers',
        steps: [
          'Go to Managers in the navigation.',
          'Assign manager roles to staff who need to oversee a team.',
          'Employees can be linked to multiple managers.',
          'Managers can approve time entries, view payroll for their team, manage schedules, and approve PTO requests.',
        ],
      },
    ],
  },
  {
    id: 'time-entry',
    icon: <IcoClock size={28} color="#22c55e"/>,
    title: 'Time Entry',
    roles: 'all',
    blocks: [
      {
        heading: 'Recording Time',
        steps: [
          'Go to Time Entry in the navigation.',
          'Select the employee and date range you want to view.',
          'Click Add Entry to log hours worked, lunch break (in minutes), and mileage.',
          'The system automatically calculates net hours, overtime, mileage reimbursement, and total pay.',
          'Time entries are used to generate payroll reports.',
        ],
      },
      {
        heading: 'Editing Time Entries',
        steps: [
          'Admins and managers can edit any time entry by clicking the edit icon on the entry row.',
          'Update any field — date, hours, lunch, mileage, or pay overrides.',
          'Changes are saved immediately and reflected in payroll calculations.',
        ],
      },
    ],
  },
  {
    id: 'payroll',
    icon: <IcoDollar size={28} color="#22c55e"/>,
    title: 'Payroll',
    roles: ['superadmin', 'adminassistant', 'manager'],
    blocks: [
      {
        heading: 'Who Can Access Payroll',
        steps: [
          'Payroll is accessible to Super Admins and Admin Assistants by default.',
          'Managers can be granted access to payroll through your organization\'s settings — contact your Super Admin to enable this.',
          'When enabled, managers can view payroll data for their assigned team members only.',
        ],
      },
      {
        heading: 'Running a Payroll Report',
        tips: 'Available to Super Admins, Admin Assistants, and Managers (if enabled by your org).',
        steps: [
          'Go to Payroll in the navigation.',
          'Select a start date and end date for the pay period.',
          'The summary table shows each employee\'s total hours, overtime, mileage, and gross pay.',
          'Click any employee row to view their full pay breakdown for the period.',
        ],
      },
      {
        heading: 'Exporting Payroll Data',
        steps: [
          'Click Export ADP on the Payroll page to download a CSV formatted for ADP payroll processing.',
          'The export includes all employees\' hours, regular pay, overtime pay, and mileage reimbursement.',
          'From an individual employee\'s payroll detail view, you can also export their entries as a CSV.',
        ],
      },
      {
        heading: 'Overtime & Pay Calculations',
        steps: [
          'Overtime rules are configured in Settings → System Preferences.',
          'You can set daily overtime (e.g., after 8 hrs/day) or weekly overtime (e.g., after 40 hrs/week).',
          'Mileage reimbursement rate is also set in System Preferences.',
          'Salaried employees can be configured to skip overtime calculations.',
        ],
      },
    ],
  },
  {
    id: 'pto',
    icon: <IcoUmbrella size={28} color="#22c55e"/>,
    title: 'PTO & Time Off',
    roles: 'all',
    blocks: [
      {
        heading: 'Submitting a Time-Off Request',
        steps: [
          'Any logged-in employee can submit a PTO or sick time request.',
          'Go to PTO in the navigation and click New Request.',
          'Choose PTO or Sick Time, select the date range, and add an optional note.',
          'Submit the request — it will show as Pending until a manager or admin reviews it.',
          'You\'ll be notified once your request is approved or denied.',
        ],
      },
      {
        heading: 'Approving or Denying Requests',
        tips: 'Available to Managers, Admin Assistants, and Super Admins.',
        steps: [
          'Pending requests appear in the PTO section under Pending Approvals.',
          'Click Approve or Deny on any request.',
          'Approved time is automatically deducted from the employee\'s available balance.',
          'The employee is notified of the decision.',
        ],
      },
      {
        heading: 'Viewing Employee PTO Balances',
        tips: 'Available to Super Admins and Admin Assistants.',
        steps: [
          'Go to PTO in the navigation to see a summary of each employee\'s available PTO and sick time.',
          'Balances reflect approved requests that have already been deducted.',
          'Manager access to employee balances is controlled by your organization settings.',
        ],
      },
      {
        heading: 'PTO Policies',
        tips: 'Configured by Super Admins in Settings.',
        steps: [
          'Balances accrue based on the policy set in Settings → Time Off Policy.',
          'Policies can differ by employment type — Full-time, Part-time, and Salaried employees can each have different accrual rates.',
          'Changes to the policy apply to future accruals only.',
        ],
      },
    ],
  },
  {
    id: 'schedule',
    icon: <IcoCalendar size={28} color="#22c55e"/>,
    title: 'Scheduling',
    roles: 'all',
    blocks: [
      // ── 1. Overview ──────────────────────────────────────────────────────
      {
        heading: 'How the Schedule is Organized',
        visual: <SvgScheduleGrid/>,
        steps: [
          'The schedule is a weekly grid — rows are your employees and columns are the days of the week.',
          'Each cell shows that employee\'s shift for that day. An empty cell means no shift has been set.',
          'Use the prev and next arrows at the top to move between weeks, or click Today to jump back to the current week.',
          'The period label in the center shows which week you are currently viewing.',
        ],
      },
      // ── 2. Setting up a shift ─────────────────────────────────────────────
      {
        heading: 'Setting Up a Shift',
        visual: <SvgGridCell/>,
        steps: [
          'Find the employee\'s row and the column for the day you want to schedule.',
          'Click inside the cell. Two time fields appear — Start and End.',
          'Type the start time (e.g. 09:00) and end time (e.g. 17:00) using 24-hour or 12-hour format.',
          'The cell updates automatically as you type — there is no separate Save button for individual cells.',
          'To remove a shift, clear both time fields or use the trash icon on that day.',
        ],
      },
      // ── 3. Per-day controls ───────────────────────────────────────────────
      {
        heading: 'Per-Day Controls',
        tips: 'These controls appear on each day cell when you hover or open the cell for editing.',
        items: [
          <RefItem
            chip={<BtnChip bg="#f0fdf4" color="#15803d" border="#86efac"><SvgToggleOff/> Mark Off</BtnChip>}
            desc="Marks the employee as scheduled off for that day. The cell turns grey and the shift is ignored in payroll. Toggle it again to remove the off marker."
          />,
          <RefItem
            chip={<BtnChip bg="#fef2f2" color="#ef4444" border="#fca5a5"><IcoTrash size={13}/></BtnChip>}
            desc="Clears the entire day's shift for that employee — removes both the start time, end time, and any off marker. Use this if you added a shift by mistake."
          />,
        ],
      },
      // ── 4. Toolbar buttons ────────────────────────────────────────────────
      {
        heading: 'Toolbar Buttons',
        tips: 'The toolbar appears above the schedule grid. Buttons are visible to managers and admins.',
        items: [
          <RefItem
            chip={<><BtnChip><SvgArrowLeft/></BtnChip><span style={{margin:'0 4px',fontSize:'0.8rem',color:'#6b7280'}}/><BtnChip><SvgArrowRight/></BtnChip></>}
            desc="Previous / Next — navigate one week backward or forward."
          />,
          <RefItem
            chip={<BtnChip bg="#e0e7ff" color="#4338ca">Today</BtnChip>}
            desc="Jumps back to the current week immediately, no matter how far you have navigated."
          />,
          <RefItem
            chip={<BtnChip><IcoSave size={13}/> Save as Template</BtnChip>}
            desc="Saves the current week's schedule layout as a named reusable template. You will be prompted to enter a template name. Templates are useful for recurring shift patterns."
          />,
          <RefItem
            chip={<BtnChip bg="#f3f4f6" color="#374151" border="#e5e7eb"><IcoFolder size={13} color="#374151"/> My Templates</BtnChip>}
            desc="Opens the template library. From here you can load a saved template onto the current week, update an existing template, or delete templates you no longer need. A green badge shows how many templates you have saved."
          />,
          <RefItem
            chip={<BtnChip bg="linear-gradient(135deg,#10b981,#059669)"><IcoRefreshCw size={13}/> Update This Week + Future</BtnChip>}
            desc="Saves the current week's schedule to the server and also sets it as the default pattern for all future weeks. Use this when you want every upcoming week to start from this layout."
          />,
          <RefItem
            chip={<BtnChip bg="#f0fdf4" color="#15803d" border="#86efac"><IcoGrid size={13} color="#15803d"/> View Defaults</BtnChip>}
            desc="Opens a panel showing which employees have a default schedule saved. Default schedules are automatically applied to new weeks when no specific schedule has been created yet."
          />,
          <RefItem
            chip={<BtnChip bg="linear-gradient(135deg,#6366f1,#4f46e5)"><IcoBarChart size={13}/> Submit All to Payroll</BtnChip>}
            desc="Submits the entire week's schedule to payroll for all employees at once. This button is only active once all individual employee submissions have been reviewed and approved. If it shows Pending Approval in grey, one or more submissions still need to be approved first."
          />,
        ],
      },
      // ── 5. Employee drill panel ───────────────────────────────────────────
      {
        heading: 'The Employee Drill Panel',
        steps: [
          'Click on any employee\'s name in the schedule grid to open their individual drill panel on the right.',
          'The drill panel shows that employee\'s full week at a glance and gives you per-employee actions.',
          'Changes you make in the drill panel are reflected in the main grid in real time.',
          'Click the employee\'s name again, or click anywhere outside the panel, to close it.',
        ],
      },
      {
        heading: 'Drill Panel Buttons',
        tips: 'These buttons appear in the bottom of the drill panel when an employee is selected.',
        items: [
          <RefItem
            chip={<BtnChip bg="linear-gradient(135deg,#22c55e,#0f4d2e)"><IcoCheck size={13}/> Update This Week</BtnChip>}
            desc="Saves the changes you made to this employee's schedule for the current week only. Future weeks and the default pattern are not affected."
          />,
          <RefItem
            chip={<BtnChip bg="linear-gradient(135deg,#10b981,#059669)"><IcoRefreshCw size={13}/> Update This Week + Future</BtnChip>}
            desc="Saves this week's changes and also updates the default schedule for this employee going forward. All future weeks will use this new pattern as the starting point."
          />,
          <RefItem
            chip={<BtnChip bg="linear-gradient(135deg,#6366f1,#4f46e5)"><IcoBarChart size={13}/> Submit to Payroll</BtnChip>}
            desc="Submits only this employee's hours to payroll. Useful when you need to submit one person at a time instead of the whole team at once. If a payroll entry already exists for that week, the button changes to Overwrite to confirm you want to replace it."
          />,
          <RefItem
            chip={<BtnChip bg="#f0f9ff" color="#0369a1" border="#bae6fd"><IcoClipboard size={13} color="#0369a1"/> Update Template</BtnChip>}
            desc="Saves the current shifts back to the template that was last loaded for this employee. The button only appears when a template has been loaded. Use it to keep your template up to date as schedules evolve."
          />,
        ],
      },
      // ── 6. Templates ─────────────────────────────────────────────────────
      {
        heading: 'Working with Templates',
        steps: [
          'Templates let you save a schedule layout once and reuse it every week without re-entering times.',
          'To save a new template: click Save as Template in the toolbar, type a name, and click Save.',
          'To load a template: click My Templates, find the template you want, and click Load. The shifts are applied to the current week immediately.',
          'You can have multiple templates — for example, a Summer Schedule and a Winter Schedule, or one per department.',
          'To update a template after making changes: open the employee drill panel, make your edits, then click Update Template.',
          'Templates only store shift times. Employee assignments, off days, and individual overrides are not part of the template.',
        ],
      },
    ],
  },
  {
    id: 'settings',
    icon: <IcoSettings size={28} color="#22c55e"/>,
    title: 'Settings',
    roles: ['superadmin'],
    blocks: [
      {
        heading: 'Organization Settings',
        steps: [
          'Click your name in the top-right corner and select Organization & Labels.',
          'Update your organization name, timezone, and display preferences.',
          'Customize labels to match your business terminology.',
        ],
      },
      {
        heading: 'System Preferences',
        steps: [
          'Under System Preferences, configure overtime rules — daily (e.g., after 8 hrs) or weekly (e.g., after 40 hrs).',
          'Set your mileage reimbursement rate per mile.',
          'Enable or disable features like schedule management and mileage tracking.',
        ],
      },
      {
        heading: 'Time Off Policy',
        steps: [
          'Under Time Off Policy, set how much PTO and sick time employees earn per pay period.',
          'Policies can be set separately for Full-time, Part-time, and Salaried employees.',
          'Changes apply to future accruals — existing balances are not affected.',
        ],
      },
      {
        heading: 'Subscription & Billing',
        steps: [
          'Under Subscription & Billing, view your current plan and active employee count.',
          'Upgrade, downgrade, or cancel your plan from this section.',
          'For billing questions, contact support at contact@flurrysystems.com.',
        ],
      },
    ],
  },
];

export default function TutorialPage() {
  const { user, isSuperAdmin } = useAuth();
  const role = user?.role;

  const visible = SECTIONS.filter(s => {
    if (s.roles === 'all') return true;
    if (Array.isArray(s.roles)) {
      if (s.roles.includes('superadmin') && isSuperAdmin()) return true;
      return s.roles.includes(role);
    }
    return false;
  });

  const [activeId, setActiveId] = useState(visible[0]?.id || 'getting-started');
  const current = visible.find(s => s.id === activeId) || visible[0];

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <div className="welcome-section">
          <h1>Help & Tutorial</h1>
          <p className="subtitle">Step-by-step guides for everything in ClockEarnings</p>
        </div>

        <div className="tutorial-layout">
          {/* Sidebar */}
          <aside className="tutorial-sidebar">
            {visible.map(s => (
              <button
                key={s.id}
                className={`tutorial-nav-btn${activeId === s.id ? ' active' : ''}`}
                onClick={() => setActiveId(s.id)}
              >
                <span className="tutorial-nav-icon">{s.icon}</span>
                {s.title}
              </button>
            ))}
          </aside>

          {/* Main content */}
          <div className="tutorial-main">
            {current && (
              <>
                <div className="tutorial-section-header">
                  <span className="tutorial-section-icon">{current.icon}</span>
                  <h2>{current.title}</h2>
                </div>

                {current.blocks.map((block, i) => (
                  <div key={i} className="tutorial-block">
                    <h3 className="tutorial-block-heading">{block.heading}</h3>

                    {block.tips && (
                      <p className="tutorial-tip">{block.tips}</p>
                    )}

                    {/* Optional SVG diagram */}
                    {block.visual && (
                      <div style={{ margin: '0.75rem 0 1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', overflowX: 'auto' }}>
                        {block.visual}
                      </div>
                    )}

                    {/* Numbered steps */}
                    {block.steps && (
                      <ol className="tutorial-steps">
                        {block.steps.map((step, j) => (
                          <li key={j}>{step}</li>
                        ))}
                      </ol>
                    )}

                    {/* Button / item reference list (no numbers) */}
                    {block.items && (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {block.items.map((item, j) => (
                          <div key={j}>{item}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
